'use client';

import dynamic from 'next/dynamic';

import { useSmartModal } from '@/hooks/useSmartModal';
import { T_SmartModalProps } from '@/ts/types/T_Overlay';

const BaseDialog = dynamic(() => import('./BaseDialog'), {
  ssr: false
});

const BaseSheet = dynamic(() => import('./BaseSheet'), {
  ssr: false
});

export function SmartModal({
  open,
  onOpenChange,
  trigger,
  dialogType = 'auto',
  variant,
  behavior,
  a11yTitle = 'Modal',
  showClose = false,
  className,
  children,
  mobileContent,
  desktopContent,
  tabletContent,
  fallbackContent,
  handleBarVariant = 'default'
}: T_SmartModalProps) {
  // Get modal configuration directly
  const {
    modalType: _modalType,
    modalPosition,
    content,
    shouldUseSheet,
    shouldUseDialog
  } = useSmartModal({
    dialogType,
    behavior,
    children,
    mobileContent,
    desktopContent,
    tabletContent,
    fallbackContent
  });

  // Render Dialog
  if (shouldUseDialog) {
    return (
      <BaseDialog
        open={open}
        onOpenChange={onOpenChange}
        trigger={trigger}
        variant={variant}
        size="md"
        a11yTitle={a11yTitle}
        showClose={showClose}
        className={className}
      >
        {content}
      </BaseDialog>
    );
  }

  // Render Sheet
  if (shouldUseSheet) {
    return (
      <BaseSheet
        open={open}
        onOpenChange={onOpenChange}
        trigger={trigger}
        variant={variant}
        showClose={showClose}
        a11yTitle={a11yTitle}
        className={className}
        position={modalPosition === 'right' ? 'right' : 'bottom'}
        handleBarVariant={handleBarVariant}
      >
        {content}
      </BaseSheet>
    );
  }

  // Fallback to Dialog for tablets and other cases
  return (
    <BaseDialog
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      showClose={showClose}
      variant={variant}
      size="md"
      a11yTitle={a11yTitle}
      className={className}
    >
      {content}
    </BaseDialog>
  );
}
